import * as THREE from 'three';
import { Quaternion, Vector3 } from 'three';
import Engine from "./Engine";
import {GroupsManager} from './GroupsManager';
import Input from './Input';
import Scenario from './scenarioElements/Scenario';
import { EffectorTitleMap} from "./timeline";
import TimelineChannel from './timeline/TimelineChannel';
import {TimelineFragment} from './timeline/TimelineFragment';
import TimelineObject from './timeline/TimelineObject';
import Utils from './Utilities';
import MathUtils from './utilities/MathUtils';
import TransformUtils from './utilities/TransformUtils';
import ActionManager from './ActionManager';
import ARTracker from './ARTracker';
import store, { StoreActions } from '../state/store';
import { CustomEventTypes, Space, TimelineEffectorTypes } from '../globals';
import TimelineEditorUtilities from "../components/timeline/TimelineEditorUtilities";
import AddMultipleTimelineElementsAction from "../actions/timelineEditor/AddMultipleTimelineElementsAction";

const defaultRecordedEffectorLength = 1;

export default class SceneRecorder
{
	constructor()
	{
		this.handleTransformRecordEvent = this.handleTransformRecordEvent.bind(this);
		this.handleSelectionChanged = this.handleSelectionChanged.bind(this);

		this.startFrame = 0;
		this.isRecording = false;
		this.guidToAnimationData = new Map();
		this.generatedTimelineObjects = new Map();
		this.aroundPivotDummy = new THREE.Object3D();

		this.unsubscribe = store.subscribe(() =>
		{
			const storeRecording = store.getState().timelineModule.editorData.isRecording;
			if (storeRecording !== this.isRecording)
			{
				this.isRecording = storeRecording;
				if (this.isRecording)
				{
					this.onRecordStart();
				}
				else
				{
					this.onRecordEnd();
				}
			}
		});
	}

	defaultTimebarLength = () => (store.getState().preferencesModule.editorPreferences.defaultTimebarLength || defaultRecordedEffectorLength);

	onRecordStart()
	{
		const timeline = Scenario.currentStep().timeline;
		if(!timeline) return;

		Engine.getInstance().getGizmoManager().exitFreeMoveMode();
		document.addEventListener(CustomEventTypes.onTransformRecordEvent, this.handleTransformRecordEvent);
		document.addEventListener(CustomEventTypes.objectSelectionChanged, this.handleSelectionChanged);
		this.handleSelectionChanged();

		this.startFrame = store.getState().timelineModule.editorData.currentFrame;
		setTimeout(() => {store.dispatch(StoreActions.workspace.setActiveWindow({ id: Engine.getInstance().getActiveSceneViewId() }));}, 0 );
	}

	onRecordEnd()
	{
		document.removeEventListener(CustomEventTypes.onTransformRecordEvent, this.handleTransformRecordEvent);
		document.removeEventListener(CustomEventTypes.objectSelectionChanged, this.handleSelectionChanged);
		const timeline = Scenario.currentStep().timeline;
		if (this.generateAnimations())
		{
			if(timeline)
			{
				const fps = timeline.fps;
				const newTime = (this.startFrame / fps) + this.defaultTimebarLength();
				store.dispatch(StoreActions.timeline.setCurrentFrame(newTime * fps));
				timeline.update(newTime);
			}
		}
		else
		{
			if(timeline) store.dispatch(StoreActions.timeline.setCurrentFrame(this.startFrame));
		}

		ActionManager.removeAllTemporaryActions();
	}

	handleSelectionChanged(e)
	{
		const timeline = Scenario.currentStep().timeline;
		const currentSelection = Engine.getInstance().getPickingManager().getCurrentSelection();
		if(!timeline || !currentSelection.objects.length) return;

		if(currentSelection.groupGuid)
		{
			if(!this.guidToAnimationData.has(currentSelection.groupGuid))
			{
				const groupData =
					{
						timeline : timeline,
						groupGuid: currentSelection.groupGuid,
						groupObjects: [...currentSelection.objects],
						initialPositions: [],
						initialQuaternions: [],
						initialScales: [],
						parentCount: -1,
						animations: [],
					}
				for (const selectedObject of currentSelection.objects)
				{
					groupData.initialPositions.push(new Vector3().copy(selectedObject.position));
					groupData.initialQuaternions.push(new Quaternion().copy(selectedObject.quaternion));
					groupData.initialScales.push(new Vector3().copy(selectedObject.scale));
				}
				this.guidToAnimationData.set(currentSelection.groupGuid, groupData);
			}
		}

		for (const selectedObject of currentSelection.objects)
		{
			if(this.guidToAnimationData.has(selectedObject.userData.guid)) continue;

			let parentCount = 0;
			let parent = selectedObject.parent;
			while(parent)
			{
				parentCount++;
				parent = parent.parent;
			}
			const objectData =
				{
					timeline : timeline,
					objectRef: selectedObject,
					initialPosition: new Vector3().copy(selectedObject.position),
					initialQuaternion: new Quaternion().copy(selectedObject.quaternion),
					initialScale: new Vector3().copy(selectedObject.scale),
					parentCount: parentCount,
					animations: [],
				}
			this.guidToAnimationData.set(selectedObject.userData.guid, objectData);
		}

	}

	addAnimationRecord(animationProps, animationList)
	{
		switch(animationProps.animationType)
		{
			case TimelineEffectorTypes.TransformRotatePivot:
				animationList.push({
					type: animationProps.animationType,
					rotationPivot: animationProps.rotationPivot,
					rotationAxis: animationProps.rotationAxis,
					rotationAngle: animationProps.rotationAngle,
					associatedAction: animationProps.associatedAction});
				break;
			case TimelineEffectorTypes.TransformTranslate:
				if (animationList.filter(function(animation) { return animation.type === animationProps.animationType }).length === 0)
				{
					animationList.push({type: animationProps.animationType, space: animationProps.space});
				}
				else
				{
					//we can't mix local & world space animations as the final animation has to be bound to one space.
					//mixing world & local animations should always default to local space.
					for(const animation of animationList)
					{
						if(animation.type === TimelineEffectorTypes.TransformTranslate)
						{
							if(animationProps.space !== animation.space)
							{
								animation.space = Space.Local;
							}
							break;
						}
					}
				}
				break;
			default:
				if (animationList.filter(function(animation) { return animation.type === animationProps.animationType }).length === 0)
				{
					animationList.push({type: animationProps.animationType});
				}
				break;
		}
	}

	handleTransformRecordEvent(e)
	{
		const currentSelection = Engine.getInstance().getPickingManager().getCurrentSelection();
		const groupGuid = currentSelection.groupGuid;

		if(groupGuid && e.detail.animationType !== TimelineEffectorTypes.TransformRotatePivot && e.detail.animationType !== TimelineEffectorTypes.TransformScale)
		{
			this.addAnimationRecord(e.detail, this.guidToAnimationData.get(groupGuid).animations);
		}
		else
		{
			for (const object of currentSelection.objects)
			{
				this.addAnimationRecord(e.detail, this.guidToAnimationData.get(object.userData.guid).animations);
			}
		}
	}

	update()
	{
		if (this.isRecording && Input.getKeybindDown(Input.KeyBindings.CreateTimelineRecord))
		{
			this.generateKeyframe();
		}
	}

	generateKeyframe()
	{
		if (this.generateAnimations())
		{
			const timeline = Scenario.currentStep().timeline;
			const fps = timeline.fps;
			const newTime = (this.startFrame / fps) + this.defaultTimebarLength();
			timeline.update(newTime);
			store.dispatch(StoreActions.timeline.setCurrentFrame(newTime * fps));
			this.startFrame = newTime * fps;
		}
		this.handleSelectionChanged();
	}

	getOrCreateTimelineObject(timeline, guid, changesToRecord)
	{
		if (!this.generatedTimelineObjects.has(guid))
		{
			const isGroup = GroupsManager.getGroupByGuid(guid);
			let timelineObject = isGroup ? TimelineEditorUtilities.findTimelineObjectFromGroupGuid(guid) : TimelineEditorUtilities.findTimelineObjectFromSceneObjectGuid(guid);
			if (!timelineObject)
			{
				const track = timeline.objectsTrack;
				const timelineObjectParams = { track: track, skipChannelCreation: true };
				if (isGroup) timelineObjectParams.objectGroupGuid = guid;
				else 		timelineObjectParams.sceneObjectGuid = guid;
				timelineObject = new TimelineObject(timelineObjectParams);
				changesToRecord.push({ parent: track, element: timelineObject });
			}
			this.generatedTimelineObjects.set(guid, timelineObject);
		}

		return this.generatedTimelineObjects.get(guid);
	}

	generateAnimations()
	{
		this.guidToAnimationData = new Map([...this.guidToAnimationData].sort((a, b) => { if(a[1].parentCount <= b[1].parentCount) return -1; return 1;	}).filter(([guid, data]) => data.animations.length > 0 ));
		const changesToRecord = [];

		for (const [guid, animationData] of this.guidToAnimationData.entries())
		{
			const timeline = animationData.timeline;
			const isGroupAnimation = GroupsManager.getGroupByGuid(guid);
			let referenceObject = null;
			if(isGroupAnimation)
			{
				for(const object of animationData.groupObjects)
				{
					if(!object.userData.removedFromScene)
					{
						referenceObject = object;
						break;
					}
				}
			}
			else
			{
				referenceObject = animationData.objectRef;
			}

			if(!referenceObject || referenceObject.userData.removedFromScene || referenceObject instanceof ARTracker || !referenceObject.parent) continue;

			function animationPriorityByType(type)
			{
				switch(type)
				{
					case TimelineEffectorTypes.TransformRotatePivot: return 1;
					case TimelineEffectorTypes.TransformRotateSelf: return 2;
					case TimelineEffectorTypes.TransformScale: return 3;
					case TimelineEffectorTypes.TransformTranslate: return 4;
				}
			}
			function mergeRotationAroundPivotAnimations(animations)
			{
				for(let i = animations.length - 1; i >= 0; i--)
				{
					if(animations[i].type !== TimelineEffectorTypes.TransformRotatePivot) continue;
					if(!ActionManager.isActionActive(animations[i].associatedAction))
					{
						animations.splice(i, 1);
						continue;
					}

					for(let j = 0; j < animations.length; j++)
					{
						if(animations[j].type !== TimelineEffectorTypes.TransformRotatePivot) break;
						if(j === i || !ActionManager.isActionActive(animations[j].associatedAction) || !MathUtils.areVectorsEqual(animations[i].rotationPivot, animations[j].rotationPivot)) continue;

						const tempAxis = new THREE.Vector3(animations[i].rotationAxis.x, animations[i].rotationAxis.y, animations[i].rotationAxis.z);
						const rotation1 = new THREE.Quaternion().setFromAxisAngle(tempAxis, animations[i].rotationAngle / 180 * Math.PI);

						tempAxis.set(animations[j].rotationAxis.x, animations[j].rotationAxis.y, animations[j].rotationAxis.z);
						const rotation2 = new THREE.Quaternion().setFromAxisAngle(tempAxis, animations[j].rotationAngle / 180 * Math.PI);

						const combinedRotation = new THREE.Quaternion().multiplyQuaternions(rotation2, rotation1);
						const combinedAxisAngle = MathUtils.quaternionToAxisAngle(combinedRotation);
						animations[j].rotationAngle = combinedAxisAngle.angle;
						animations[j].rotationAxis = combinedAxisAngle.axis;
						animations.splice(i, 1);
						break;
					}
				}
			}

			animationData.animations.sort((a, b) => { if(animationPriorityByType(a.type) <= animationPriorityByType(b.type)) return -1; return 1;});
			mergeRotationAroundPivotAnimations(animationData.animations);

			const referenceObjectInitialPosition = isGroupAnimation ? animationData.initialPositions[0] : animationData.initialPosition;
			const referenceObjectInitialQuaternion = isGroupAnimation ? animationData.initialQuaternions[0] : animationData.initialQuaternion;
			const referenceObjectInitialScale = isGroupAnimation ? animationData.initialScales[0] : animationData.initialScale;

			referenceObject.parent.add(this.aroundPivotDummy);
			this.aroundPivotDummy.position.copy(referenceObjectInitialPosition);
			this.aroundPivotDummy.quaternion.copy(referenceObjectInitialQuaternion);
			this.aroundPivotDummy.scale.copy(referenceObjectInitialScale);

			const fps = timeline.fps;
			const startingTime = this.startFrame / fps;
			const timebarLength = this.defaultTimebarLength();
			let hasRotationAroundPivot = false;

			for(const animation of animationData.animations)
			{
				switch(animation.type)
				{
					case TimelineEffectorTypes.TransformRotatePivot:
						{
							if (animation.rotationAngle !== 0.0 && (animation.rotationAxis.x !== 0.0 || animation.rotationAxis.y !== 0.0 || animation.rotationAxis.z !== 0.0 ))
							{
								const timelineObject = this.getOrCreateTimelineObject(timeline, guid, changesToRecord);
								const newChannel = new TimelineChannel(Utils.getUniqueElementName(timelineObject.channels, "Rotate Around Pivot Channel"), timelineObject);
								const fragmentName = Utils.getUniqueElementName(newChannel.fragments, EffectorTitleMap.get(TimelineEffectorTypes.TransformRotatePivot));
								const newFragment = new TimelineFragment(fragmentName, newChannel, TimelineEffectorTypes.TransformRotatePivot, startingTime, startingTime + timebarLength);

								newFragment.effector.rotationPivot.set(animation.rotationPivot.x, animation.rotationPivot.y, animation.rotationPivot.z);
								newFragment.effector.axis.set(animation.rotationAxis.x, animation.rotationAxis.y, animation.rotationAxis.z);
								newFragment.effector.angle = animation.rotationAngle;

								changesToRecord.push({ parent: timelineObject, element: newChannel });
								changesToRecord.push({ parent: newChannel, element: newFragment });

								TransformUtils.rotateAround(this.aroundPivotDummy, newFragment.effector.rotationPivot, newFragment.effector.axis, newFragment.effector.angle / 180.0 * Math.PI);
								hasRotationAroundPivot = true;
							}
						}
						break;
					case TimelineEffectorTypes.TransformRotateSelf:
						{
							const initialQuaternion = hasRotationAroundPivot ? this.aroundPivotDummy.quaternion : referenceObjectInitialQuaternion;
							const targetQuaternion = referenceObject.quaternion;
							const deltaRotation = MathUtils.getShortestDeltaQuaternion(initialQuaternion, targetQuaternion);
							const axisAngle = MathUtils.quaternionToAxisAngle(deltaRotation);
							if (axisAngle.angle !== 0.0 && (axisAngle.axis.x !== 0.0 || axisAngle.axis.y !== 0.0 ||axisAngle.axis.z !== 0.0 ))
							{
								const timelineObject = this.getOrCreateTimelineObject(timeline, guid, changesToRecord);
								const newChannel = new TimelineChannel(Utils.getUniqueElementName(timelineObject.channels, "Rotate Channel"), timelineObject);
								const fragmentName = Utils.getUniqueElementName(newChannel.fragments, EffectorTitleMap.get(TimelineEffectorTypes.TransformRotateSelf));
								const newFragment = new TimelineFragment(fragmentName, newChannel, TimelineEffectorTypes.TransformRotateSelf, startingTime, startingTime + timebarLength);

								newFragment.effector.axis.set(axisAngle.axis.x, axisAngle.axis.y, axisAngle.axis.z);
								newFragment.effector.angle = axisAngle.angle;

								changesToRecord.push({ parent: timelineObject, element: newChannel });
								changesToRecord.push({ parent: newChannel, element: newFragment });
							}
						}
						break;
					case TimelineEffectorTypes.TransformScale:
						{
							const deltaScale = new THREE.Vector3().subVectors(referenceObject.scale, referenceObjectInitialScale);

							if (deltaScale.length() > 0.000001)
							{
								const timelineObject = this.getOrCreateTimelineObject(timeline, guid, changesToRecord);
								const newChannel = new TimelineChannel(Utils.getUniqueElementName(timelineObject.channels, "Scale Channel"), timelineObject);
								const fragmentName = Utils.getUniqueElementName(newChannel.fragments, EffectorTitleMap.get(TimelineEffectorTypes.TransformScale));
								const newFragment = new TimelineFragment(fragmentName, newChannel, TimelineEffectorTypes.TransformScale, startingTime, startingTime + timebarLength);

								MathUtils.round(deltaScale);
								newFragment.effector.scale.copy(deltaScale);

								changesToRecord.push({ parent: timelineObject, element: newChannel });
								changesToRecord.push({ parent: newChannel, element: newFragment });
							}
						}
						break;
					case TimelineEffectorTypes.TransformTranslate:
						{
							const deltaTranslation = new THREE.Vector3();
							if(animation.space === Space.Local)
							{
								const inverseQuaternion = new THREE.Quaternion().copy(referenceObject.quaternion).invert();
								deltaTranslation.subVectors(referenceObject.position, hasRotationAroundPivot ? this.aroundPivotDummy.position : referenceObjectInitialPosition).applyQuaternion(inverseQuaternion);
							}
							else
							{
								deltaTranslation.copy(referenceObject.position);
								referenceObject.parent.localToWorld(deltaTranslation);
								const initialPosition = new THREE.Vector3().copy(hasRotationAroundPivot ? this.aroundPivotDummy.position : referenceObjectInitialPosition);
								referenceObject.parent.localToWorld(initialPosition);
								deltaTranslation.sub(initialPosition);
							}

							if (deltaTranslation.length() > 0.000001)
							{
								const timelineObject = this.getOrCreateTimelineObject(timeline, guid, changesToRecord);
								const newChannel = new TimelineChannel(Utils.getUniqueElementName(timelineObject.channels, "Position Channel"), timelineObject);
								const fragmentName = Utils.getUniqueElementName(newChannel.fragments, EffectorTitleMap.get(TimelineEffectorTypes.TransformTranslate));
								const newFragment = new TimelineFragment(fragmentName, newChannel, TimelineEffectorTypes.TransformTranslate, startingTime, startingTime + timebarLength);

								MathUtils.round(deltaTranslation);
								newFragment.effector.translation.copy(deltaTranslation);
								newFragment.effector.space = animation.space;

								changesToRecord.push({ parent: timelineObject, element: newChannel });
								changesToRecord.push({ parent: newChannel, element: newFragment });
							}
						}
						break;
				}
			}

			this.aroundPivotDummy.removeFromParent();

			if(!isGroupAnimation)
			{
				animationData.objectRef.position.copy(referenceObjectInitialPosition);
				animationData.objectRef.quaternion.copy(referenceObjectInitialQuaternion);
				animationData.objectRef.scale.copy(referenceObjectInitialScale);
			}
			else
			{
				for(let i = 0; i < animationData.groupObjects.length; i++)
				{
					const object = animationData.groupObjects[i];
					object.position.copy(animationData.initialPositions[i]);
					object.quaternion.copy(animationData.initialQuaternions[i]);
					object.scale.copy(animationData.initialScales[i]);
				}
			}

		}

		this.guidToAnimationData.clear();
		this.generatedTimelineObjects.clear();
		new AddMultipleTimelineElementsAction(changesToRecord);
		return changesToRecord.length;
	}
}
